import { useEffect, useMemo, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { Button, DatePicker, Popover, formikHelpers, lucideIcons } from '@round/creatorbase-ui';
import { formatDeadline, fromDeadlineValue, getDeadlineRange, toDeadlineValue } from '../../helpers';
import { FormValues } from '../../types';

const { Field: FormikField } = formikHelpers;

const LABEL = 'Deadline';

/** What the trigger reads while nothing is picked — and what the backend then decides for itself. */
const NO_DEADLINE_LABEL = 'Three months from today';

type Props = {
    disabled?: boolean;
};

/**
 * The picker offers only the days the endpoint accepts, so the window is taught by what can be
 * clicked rather than by a rejected submit. Picking the selected day again clears it.
 *
 * Rate pushes the earliest pickable day out to give the collection window time to run, so ticking
 * it can leave an already-picked date behind the new earliest. Rather than let that stale date sit
 * there silently accepted, it is cleared back to the unset default so the disabled-days picker
 * teaches the new window the same way it teaches the initial one.
 */
export const DeadlineField = ({ disabled }: Props) => {
    const [{ value: deadline }, , { setValue }] = useField<string | null>('deadline');
    const [{ value: outputRate }] = useField<boolean>('outputRate');
    // useField's own helpers are re-created on every render, so a stale-deadline effect keyed off
    // them would never settle — setFieldValue from context is the one that stays referentially stable.
    const { setFieldValue } = useFormikContext<FormValues>();
    const [isOpen, setIsOpen] = useState(false);
    const { earliest, latest } = useMemo(() => getDeadlineRange(outputRate), [outputRate]);

    const selected = fromDeadlineValue(deadline);

    useEffect(() => {
        const selectedDate = fromDeadlineValue(deadline);
        if (selectedDate && selectedDate < earliest) {
            setFieldValue('deadline', null);
        }
    }, [deadline, earliest, setFieldValue]);

    const handleSelect = (date: Date | undefined) => {
        setValue(date ? toDeadlineValue(date) : null);
        setIsOpen(false);
    };

    return (
        <FormikField name="deadline">
            <FormikField.Title>{LABEL}</FormikField.Title>
            <FormikField.Description>
                The last day the request keeps collecting creators, not when results arrive.
            </FormikField.Description>

            <div>
                <Popover open={isOpen} onOpenChange={setIsOpen}>
                    <Popover.Trigger asChild>
                        <Button variant="outline" size="xs" disabled={disabled}>
                            <lucideIcons.CalendarIcon />
                            {deadline ? formatDeadline(deadline) : NO_DEADLINE_LABEL}
                        </Button>
                    </Popover.Trigger>
                    <Popover.Content align="start" className="p-0">
                        <DatePicker
                            mode="single"
                            selected={selected}
                            onSelect={handleSelect}
                            disabled={{ before: earliest, after: latest }}
                            startMonth={earliest}
                            endMonth={latest}
                            defaultMonth={selected ?? earliest}
                        />
                    </Popover.Content>
                </Popover>
            </div>

            <FormikField.Error name="deadline" />
        </FormikField>
    );
};
